import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { DateTime, Duration } from 'scaleflow/utils/date-time';

export default class CapacityChartComponent extends Component {
  @service initiativeSlideover;
  @tracked initiatives = [];
  @tracked scenario = null;

  get disabled() {
    return this.initiatives.length == 0;
  }

  @action
  async loadInitiatives() {
    if (this.args.initiatives != undefined) {
      this.initiatives = await this.args.initiatives;
    }
  }

  @action
  async loadScenario() {
    if (this.args.scenario != undefined) {
      this.scenario = await this.args.scenario;
    }
  }

  @action
  clickHandler(params) {
    const initiative_id = params.seriesId;
    if (initiative_id.includes(' ')) return;
    this.initiativeSlideover.open(initiative_id);
  }

  @action
  onLinkMetricClick() {
    return this.args?.onLinkMetricClick();
  }

  get reportingWeeks() {
    const from = DateTime.from(this.args.from);
    const to = DateTime.from(this.args.to);
    const weeks = Math.ceil(
      Duration.of(to.diff(from, 'milliseconds'), 'milliseconds').asWeeks(),
    );
    const reportingWeeks = [];

    for (let i = 0; i <= weeks; i++) {
      const weekNumber = DateTime.from(this.args.from).add(i, 'weeks');
      reportingWeeks.push(weekNumber.format('YYYY-[W]WW'));
    }

    return reportingWeeks;
  }

  get series() {
    let initiatives = this.initiatives;
    const scenario = this.scenario;
    const reporting_weeks = this.reportingWeeks;
    const series = [];

    if (!scenario) return series;

    // Calculate total available capacity (100% reference)
    const totalCapacity = scenario.fte;

    // Calculate plannable (initiative) capacity threshold
    const initiativeCapacity = Math.round(
      scenario.fte * (scenario.initiative_capacity / 100),
      1,
    );

    // Calculate workload per week as percentage
    const weeklyData = reporting_weeks.map((week) => {
      // Total workload for this week
      let totalWorkload = 0;

      // Add up all initiatives active in this week, regardless of source
      initiatives.forEach((initiative) => {
        const startInRange = DateTime.from(
          initiative.startingWeek,
        ).isSameOrBefore(week);
        const endInRange = DateTime.from(initiative.endingWeek).isSameOrAfter(
          week,
        );
        if (startInRange && endInRange) {
          // All initiatives contribute to workload, even those without a source
          totalWorkload += initiative.capacityPerWeek || 0;
        }
      });

      // Calculate percentage
      const percentage =
        totalCapacity > 0 ? (totalWorkload / totalCapacity) * 100 : 0;

      return {
        week: week,
        percentage: percentage,
        value: percentage,
        raw_percentage: percentage,
        total_workload: totalWorkload,
        initiative_capacity: initiativeCapacity,
        total_capacity: totalCapacity,
      };
    });

    // Create single bar series with color based on thresholds
    const barSeries = {
      type: 'bar',
      name: 'Workload',
      barWidth: '60%',
      itemStyle: {
        color: (params) => {
          const percentage = params.data.raw_percentage;
          const initiativePercentage =
            (initiativeCapacity / totalCapacity) * 100;

          // Color based on capacity thresholds
          if (percentage > 100) {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#FF6259' }, // lighter red
                { offset: 1, color: '#E02A1F' }, // darker red
              ],
            };
          } else if (percentage > initiativePercentage) {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#FFA53F' }, // lighter orange
                { offset: 1, color: '#FF7D00' }, // darker orange
              ],
            };
          } else {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#5BEF6A' }, // lighter green
                { offset: 1, color: '#28B637' }, // darker green
              ],
            };
          }
        },
        borderRadius: [5, 5, 0, 0], // Rounded corners at the top
        shadowColor: 'rgba(0, 0, 0, 0.2)',
        shadowBlur: 5,
      },
      label: {
        show: true,
        position: 'top',
        distance: 5,
        formatter: function (params) {
          const percentage = params.data.raw_percentage;
          const initiativePercentage =
            (initiativeCapacity / totalCapacity) * 100;
          const labelText = Math.round(percentage) + '%';

          // Match label style to the bar color logic
          if (percentage > 100) {
            return '{overCapacity|' + labelText + '}';
          } else if (percentage > initiativePercentage) {
            return '{flowCapacity|' + labelText + '}';
          } else {
            return '{initiativeCapacity|' + labelText + '}';
          }
        },
        rich: {
          overCapacity: {
            color: '#E02A1F',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 12,
            padding: [2, 4],
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(224, 42, 31, 0.3)',
            shadowBlur: 3,
          },
          flowCapacity: {
            color: '#FF7D00',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 12,
            padding: [2, 4],
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(255, 125, 0, 0.3)',
            shadowBlur: 3,
          },
          initiativeCapacity: {
            color: '#28B637',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 12,
            padding: [2, 4],
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(40, 182, 55, 0.3)',
            shadowBlur: 3,
          },
        },
      },
      encode: {
        x: 'week',
        y: 'value',
      },
      data: weeklyData,
    };

    series.push(barSeries);

    // Add markers for initiative capacity threshold
    series.push({
      type: 'line',
      name: 'Plannable time',
      silent: true,
      symbol: 'none',
      itemStyle: {
        color: 'rgba(0, 0, 0, 0.5)',
      },
      lineStyle: {
        color: 'rgba(0, 0, 0, 0.5)',
        width: 1.5,
        type: 'dashed',
      },
      data: reporting_weeks.map(
        () => (initiativeCapacity / totalCapacity) * 100,
      ),
    });

    // Add threshold line for 100% capacity
    series.push({
      type: 'line',
      name: 'Total Capacity',
      silent: true,
      symbol: 'none',
      lineStyle: {
        color: 'rgba(255, 99, 71, 0.6)',
        width: 2,
      },
      data: reporting_weeks.map(() => 100),
    });

    return series;
  }

  get currentWeekNumber() {
    return DateTime.now().format('GGGG-[W]WW');
  }

  get startingWeekLower() {
    return DateTime.from(this.args.from).format('GGGG-[W]WW');
  }

  get startingWeekUpper() {
    return DateTime.from(this.args.to).format('GGGG-[W]WW');
  }
}
